// import { toQuery, bytesToBase64 } from "./utils.js";

import { baseurl, backend_url } from './setting.js';

const corsmode = 'cors';
const LogoutUrl = baseurl + '/idp/startSLO.ping';
const app_id = ['DPLP', 'OCOM'];

let isRefreshing = false;
let refreshPromise = null;

export const StorageKey = {
  auth_logInState: 'auth.logInState',
  auth_AccessToken: 'auth.access_token',
  auth_ExpiresAt: 'auth.expires_at',
  auth_RefreshToken: 'auth.refresh_token',
  userInfo_sub: 'userInfo.sub',
  userInfo_name: 'userInfo.name',
  userInfo_app_id: 'userInfo.app_id',
  userInfo_ett_group: 'userInfo.entitlement_group',
  userInfo_email: 'userInfo.email',
  token_GivenName: 'token.given_name',
  token_FamilyName: 'token.family_name',
  token_Country: 'token.country',
  token_SupplierId: 'token.supplier_id',
  token_OrgAncestors: 'token.org_ancestors',
};

export const LogInState = {
  unauthorized: 'unauthorized',
  authorized: 'authorized',
  getToken: 'getToken',
  setUserInfo: 'setUserInfo',
};

export class DataService {
  static async getAuthorizationHeader() {
    const token = localStorage.getItem(StorageKey.auth_AccessToken);
    if (token && token !== 'undefined' && token !== 'null') {
      return 'Bearer ' + token;
    }
    console.warn('[Auth] Access token missing or invalid');
    return null;
  }

  static authorized() {
    const logInStatus = localStorage.getItem(StorageKey.auth_logInState);
    return logInStatus === LogInState.authorized;
  }

  static isUnauthorized() {
    const logInStatus = localStorage.getItem(StorageKey.auth_logInState);
    return logInStatus === LogInState.unauthorized;
  }

  static getAuthError() {
    return localStorage.getItem('auth.lastError') || null;
  }

  static async getFetchHeader(endpoint, additionalHeaders = {}) {
    const apiKeySecEP = ['/vessel', '/data'];
    const headers = new Headers();

    // Append additional headers if provided
    for (const [key, value] of Object.entries(additionalHeaders)) {
      headers.append(key, value);
    }

    // ApiKey secured Endpoints
    for (let i = 0; i < apiKeySecEP.length; i++) {
      if (endpoint.startsWith(apiKeySecEP[i])) {
        let ApiKey = await DataService.getApiKey();
        let ApiKeyHeader = await DataService.getApiHeader();

        headers.append(ApiKeyHeader, ApiKey);
        return headers;
      }
    }

    // Bearer authorized Endpoints
    let authHeader = await DataService.getAuthorizationHeader();
    if (authHeader) {
      headers.append('Authorization', authHeader);
    }

    return headers;
  }

  static async fetchGet(url, headers) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: corsmode,
        headers: headers,
      });

      if (!response.ok) {
        console.error(`[Auth] HTTP ${response.status} from ${url}`);
        // Return an error object so callers can inspect status
        return { _error: true, status: response.status };
      }

      return await response.json();
    } catch (error) {
      console.error('[Auth] Network error:', error);
      return { _error: true, status: 0, message: error.message };
    }
  }

  static async fetchPost(url, headers, body) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: corsmode,
        headers: headers,
        body: body,
      });

      if (!response.ok) {
        console.error(`[Auth] HTTP ${response.status} from ${url}`);
        return { _error: true, status: response.status };
      }

      return await response.json();
    } catch (error) {
      console.error('[Auth] Network error:', error);
      return { _error: true, status: 0, message: error.message };
    }
  }

  static async setExpiredAt(authDate, addSeconds) {
    let expiresAtDate = new Date(
      authDate.setSeconds(authDate.getSeconds() + addSeconds)
    );
    var expiresAt = (new Date(expiresAtDate).getTime() / 1000) | 0;
    return expiresAt;
  }

  static async refreshToken(status) {
    let runs = 0;
    let ExpiresAt = localStorage.getItem(StorageKey.auth_ExpiresAt);
    let logInState = localStorage.getItem(StorageKey.auth_logInState);
    let checkvalue = parseInt(Date.now() / 1000) >= ExpiresAt;

    const delay = (ms) => new Promise((res) => setTimeout(res, ms));

    if (
      (checkvalue || status === 401) &&
      logInState === LogInState.authorized
    ) {
      if (isRefreshing) {
        while (isRefreshing) {
          runs++;
          await delay(100);
          if (runs > 100) {
            // 10 sec limit reached
            break;
          }
        }

        return refreshPromise;
      }

      isRefreshing = true;

      let auth_AccessToken = localStorage.getItem(StorageKey.auth_AccessToken);
      let refreshToken = localStorage.getItem(StorageKey.auth_RefreshToken);

      let url = `${backend_url}/utils/refreshtoken?token=${refreshToken}`;
      const headers = {
        accept: 'application/json',
        Authorization: `Bearer ${auth_AccessToken}`,
      };
      refreshPromise = await this.fetchGet(url, headers);

      if (refreshPromise && !refreshPromise._error) {
        // save session information
        localStorage.setItem(
          StorageKey.auth_AccessToken,
          refreshPromise.access_token
        );
        localStorage.setItem(
          StorageKey.auth_RefreshToken,
          refreshPromise.refresh_token
        );
        let expiredAt = await this.setExpiredAt(
          new Date(),
          refreshPromise.expires_in
        );
        localStorage.setItem(StorageKey.auth_ExpiresAt, expiredAt);
      } else if (refreshPromise && refreshPromise._error) {
        console.warn('[Auth] Token refresh failed, clearing session');
        await this.clearStorage();
      }

      isRefreshing = false;
      refreshPromise = null;

      return refreshPromise;
    }
  }

  static async getToken() {
    let auth_logInState = sessionStorage.getItem(StorageKey.auth_logInState);

    if (
      auth_logInState === LogInState.getToken &&
      window.location.href.indexOf('?code=') > -1
    ) {
      const query = window.location.search;
      let parameters = new URLSearchParams(query);
      let code = parameters.get('code');

      const headers = {};

      let url = `${backend_url}/utils/login?code=${code}`;

      const resObj = await this.fetchGet(url, headers);

      if (resObj && !resObj._error) {
        // save session information
        localStorage.setItem(StorageKey.auth_AccessToken, resObj.access_token);
        localStorage.setItem(
          StorageKey.auth_RefreshToken,
          resObj.refresh_token
        );

        localStorage.setItem(StorageKey.auth_ExpiresAt, resObj.exp);
        localStorage.setItem(StorageKey.token_GivenName, resObj.given_name);
        localStorage.setItem(StorageKey.token_FamilyName, resObj.family_name);
        localStorage.setItem(StorageKey.token_Country, resObj.country);

        if (resObj.supplier_id) {
          localStorage.setItem(StorageKey.token_SupplierId, resObj.supplier_id);
          if (resObj.org_ancestors) {
            localStorage.setItem(
              StorageKey.token_OrgAncestors,
              resObj.org_ancestors
            );
          }
        }

        sessionStorage.setItem(
          StorageKey.auth_logInState,
          LogInState.setUserInfo
        );
      } else {
        console.error('[Auth] Token exchange failed', resObj);
        localStorage.setItem(
          'auth.lastError',
          'Token exchange failed. The authorization code may be expired. Please try again.'
        );
        localStorage.setItem(
          StorageKey.auth_logInState,
          LogInState.unauthorized
        );
        sessionStorage.removeItem(StorageKey.auth_logInState);
      }
    }
  }

  static refreshOnBackButton() {
    try {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0 && navEntries[0].type === 'back_forward') {
        DataService.clearStorage();
        window.location.reload();
      }
    } catch (e) {
      // Ignore if performance API is unavailable
    }
  }

  static async ChecklogIn() {
    await this.refreshToken();

    // Event listener for Logout event to reload page in other tabs
    window.addEventListener('storage', function (event) {
      if (event.key === 'logout-event') {
        DataService.clearStorage().then(() => {
          window.location.reload();
        });
      }
    });

    window.addEventListener('click', () => {
      DataService.refreshToken();
    });

    window.addEventListener('pageshow', function (event) {
      var historyTraversal =
        event.persisted ||
        (typeof window.performance != 'undefined' &&
          performance.getEntriesByType('navigation')[0]?.type ===
            'back_forward');
      if (historyTraversal) {
        DataService.clearStorage().then(() => {
          window.location.reload();
        });
        return;
      }
    });

    this.refreshOnBackButton();

    // If auth already completed (authorized or unauthorized), skip login flow
    let logIn_finished = localStorage.getItem(StorageKey.auth_logInState);
    if (logIn_finished) return;

    let auth_logInState = sessionStorage.getItem(StorageKey.auth_logInState);

    auth_logInState = await DataService.checkRedirect(auth_logInState);

    if (!auth_logInState) {
      sessionStorage.setItem(StorageKey.auth_logInState, LogInState.getToken);
      // Redirect to backend OAuth2 login endpoint
      window.location.href = backend_url;
    }
  }

  static async getAuth() {
    DataService.refreshOnBackButton();

    // PRD/INT
    let chkstr = 'oauth2/authorization';
    let setstr = 'api/oauth2/authorization';
    if (window.location.href.indexOf(chkstr) > -1) {
      let current = window.location.href;
      current = current.replace(chkstr, setstr);
      window.location.href = current;
    }

    let logIn_finished = localStorage.getItem(StorageKey.auth_logInState);
    if (logIn_finished) return;

    await this.getToken();
    await this.getUserInfo();
  }

  static async getUserInfo() {
    let auth_logInState = sessionStorage.getItem(StorageKey.auth_logInState);

    if (auth_logInState === LogInState.setUserInfo) {
      const access_token = localStorage.getItem(StorageKey.auth_AccessToken);
      const headers = {
        accept: 'application/json',
        Authorization: `Bearer ${access_token}`,
      };

      const url = `${backend_url}/utils/userinfo`;

      let userInfo = await this.fetchGet(url, headers);

      if (userInfo && !userInfo._error) {
        // Save user info
        localStorage.setItem(StorageKey.userInfo_sub, userInfo.sub);
        localStorage.setItem(StorageKey.userInfo_name, userInfo.name);
        localStorage.setItem(StorageKey.userInfo_app_id, userInfo.app_id);
        localStorage.setItem(
          StorageKey.userInfo_ett_group,
          userInfo.entitlement_group
        );
        localStorage.setItem(StorageKey.userInfo_email, userInfo.email);

        // Check authorization: app_id must match and entitlement_group must be non-empty
        if (userInfo.app_id && userInfo.entitlement_group) {
          if (
            app_id.includes(userInfo.app_id) === false ||
            userInfo.entitlement_group.length < 1
          ) {
            localStorage.setItem(
              StorageKey.auth_logInState,
              LogInState.unauthorized
            );
            if (app_id.includes(userInfo.app_id) === false) {
              localStorage.setItem(
                'auth.lastError',
                `Your application ID "${userInfo.app_id}" is not authorized for this application. Required: ${app_id.join(', ')}.`
              );
            } else {
              localStorage.setItem(
                'auth.lastError',
                'Your account has no entitlement groups assigned. Please contact your administrator to request access.'
              );
            }
          } else {
            localStorage.setItem(
              StorageKey.auth_logInState,
              LogInState.authorized
            );
            localStorage.removeItem('auth.lastError');
          }
        } else {
          localStorage.setItem(
            StorageKey.auth_logInState,
            LogInState.unauthorized
          );
          localStorage.setItem(
            'auth.lastError',
            'Missing app_id or entitlement_group in your identity token. Please contact your administrator.'
          );
        }
      } else {
        localStorage.setItem(
          StorageKey.auth_logInState,
          LogInState.unauthorized
        );
        const statusInfo = userInfo?._error ? ` (HTTP ${userInfo.status})` : '';
        localStorage.setItem(
          'auth.lastError',
          `Failed to retrieve user information from the identity provider${statusInfo}. Please try again later.`
        );
      }

      sessionStorage.removeItem(StorageKey.auth_logInState);

      // Remove search params (code, state) & reload to finalize
      let urlObj = new URL(window.location.href);
      urlObj.search = '';
      window.location.assign(urlObj.toString());
    }
  }

  static async clearStorage() {
    await this.revokeToken();

    localStorage.clear();
    sessionStorage.clear();
  }

  static async logout() {
    // Raise event for reload in other tabs
    localStorage.setItem('logout-event', 'logout' + Math.random());
    await DataService.clearStorage();
    window.location.href = `${LogoutUrl}`;
  }

  static async logoutAndRelogin() {
    // Clear local state only — don't do IdP SLO — immediately restart login
    await DataService.clearStorage();
    window.location.reload();
  }

  static async revokeToken() {
    let accessToken = localStorage.getItem(StorageKey.auth_AccessToken);
    if (accessToken && accessToken !== 'undefined' && accessToken !== 'null') {
      try {
        const endpoint = '/params/revoketoken';

        const headers = await DataService.getFetchHeader(endpoint, {
          'Content-Type': 'application/x-www-form-urlencoded',
        });

        const params = new URLSearchParams();
        params.append('token', accessToken);
        const url = `${backend_url}${endpoint}?${params.toString()}`;
        await this.fetchGet(url, headers);
      } catch (e) {
        console.warn('[Auth] Token revocation failed (non-critical):', e);
      }
    }
  }

  static getUserRoles(roles) {
    let value = localStorage.getItem(StorageKey.userInfo_ett_group);
    if (value) {
      const array = value.split(',');

      let i = 0;
      while (i < array.length) {
        array[i] = array[i].substring(5);
        i++;
      }

      roles = JSON.stringify(array);
    }
    return roles;
  }

  static async getApiHeader() {
    const accessToken = localStorage.getItem(StorageKey.auth_AccessToken);
    const header = {
      Authorization: `Bearer ${accessToken}`,
    };

    let url = `${backend_url}/params/getapiheader`;
    const responseApiKeyHeader = await this.fetchGet(url, header);
    if (responseApiKeyHeader && !responseApiKeyHeader._error) {
      return responseApiKeyHeader.value;
    }
    return null;
  }

  static async getApiKey() {
    const accessToken = localStorage.getItem(StorageKey.auth_AccessToken);
    const header = {
      Authorization: `Bearer ${accessToken}`,
    };

    let url = `${backend_url}/params/getapikey`;
    const responseApiKey = await this.fetchGet(url, header);
    if (responseApiKey && !responseApiKey._error) {
      return responseApiKey.value;
    }
    return null;
  }

  static async checkRedirect(auth_logInState) {
    let strCode = '?code=';
    let strOauth2 = 'oauth2/authorization';
    let strOauth2Api = 'api/oauth2/authorization';

    // Redirect INT/PRD
    if (
      auth_logInState === LogInState.getToken &&
      window.location.href.indexOf(strCode) === -1
    ) {
      if (
        window.location.href.indexOf(strOauth2Api) === -1 &&
        window.location.href.indexOf(strOauth2) > -1
      ) {
        let current = window.location.href;
        current = current.replace(strOauth2, strOauth2Api);
        window.location.href = current;
      } else {
        sessionStorage.removeItem(StorageKey.auth_logInState);
        return null;
      }
    }
    return auth_logInState;
  }

  /**
   * Bootstrap the current user: ensure a user record exists in the backend.
   *
   * Reads identity fields from localStorage (populated during auth), performs
   * getUser → if exists compare & update selected profile fields → if not found
   * call backend bootstrap/me fallback → re-fetch user.
   *
   * Sets sessionStorage "bootstrap.status" to "ok" or "error" and
   * "bootstrap.error" with a human-readable reason on failure.
   *
   * Returns true on success, false on failure.
   */
  static async bootstrapUser() {
    const { getUser, addUser, updateUser, bootstrapMe } =
      await import('../../features/users/index.ts');

    const emea = localStorage.getItem(StorageKey.userInfo_sub);
    if (!emea) {
      const msg = 'Bootstrap failed: no user identity (sub) in session.';
      console.error('[Bootstrap]', msg);
      sessionStorage.setItem('bootstrap.status', 'error');
      sessionStorage.setItem('bootstrap.error', msg);
      return false;
    }

    const email = localStorage.getItem(StorageKey.userInfo_email);
    const givenName =
      localStorage.getItem(StorageKey.token_GivenName)?.trim() || null;
    const familyName =
      localStorage.getItem(StorageKey.token_FamilyName)?.trim() || null;
    const country =
      localStorage.getItem(StorageKey.token_Country)?.trim() || null;
    const supplier = localStorage.getItem(StorageKey.token_SupplierId);
    const rolesString = localStorage.getItem(StorageKey.userInfo_ett_group);
    const roles = rolesString
      ? rolesString.split(',').map((r) => r.trim())
      : [];
    const orgAncString = localStorage.getItem(StorageKey.token_OrgAncestors);
    const organcestors = orgAncString
      ? orgAncString.split(',').map((o) => o.trim())
      : [];

    const supplierNo =
      supplier && supplier !== 'undefined' ? supplier.trim() : null;

    const newUserData = {
      emea,
      email: email?.trim() || null,
      givenName,
      familyName,
      roles,
      organcestors,
      supplier: supplierNo ? { supplierNo } : null,
      country: country ? { countryCode: country } : null,
    };

    try {
      let existingUser;
      try {
        existingUser = await getUser(emea);
      } catch (getUserError) {
        // User not found — try backend bootstrap fallback
        const notFound =
          getUserError.message.includes('User not found') ||
          getUserError.message.includes('404') ||
          getUserError.message.includes('Something went wrong');

        if (notFound) {
          // console.log("[Bootstrap] User not found, calling backend bootstrap/me fallback");
          try {
            await bootstrapMe();
            // Re-fetch user after backend bootstrap
            existingUser = await getUser(emea);
            // console.log("[Bootstrap] User created via backend fallback");
          } catch (bootstrapError) {
            // Backend fallback failed — try direct create as last resort
            console.warn(
              '[Bootstrap] Backend fallback failed, attempting direct create:',
              bootstrapError.message
            );
            try {
              await addUser(newUserData);
              // console.log("[Bootstrap] User created via direct addUser");
              sessionStorage.setItem('bootstrap.status', 'ok');
              sessionStorage.removeItem('bootstrap.error');
              return true;
            } catch (createError) {
              const msg = `Bootstrap failed: could not create user. ${createError.message}`;
              console.error('[Bootstrap]', msg);
              sessionStorage.setItem('bootstrap.status', 'error');
              sessionStorage.setItem('bootstrap.error', msg);
              return false;
            }
          }
        } else {
          const msg = `Bootstrap failed: unexpected error fetching user. ${getUserError.message}`;
          console.error('[Bootstrap]', msg);
          sessionStorage.setItem('bootstrap.status', 'error');
          sessionStorage.setItem('bootstrap.error', msg);
          return false;
        }
      }

      // User exists — check if profile fields need updating
      if (existingUser) {
        const existingRoles = (existingUser.roles || [])
          .map((r) => r.trim())
          .sort();
        const newRoles = roles.map((r) => r.trim()).sort();
        const existingOrg = (existingUser.organcestors || [])
          .map((o) => o.trim())
          .sort();
        const newOrg = organcestors.map((o) => o.trim()).sort();

        let needsUpdate = false;
        if ((existingUser.email?.trim() || null) !== (email?.trim() || null))
          needsUpdate = true;
        if ((existingUser.givenName?.trim() || null) !== givenName)
          needsUpdate = true;
        if ((existingUser.familyName?.trim() || null) !== familyName)
          needsUpdate = true;
        if (JSON.stringify(existingRoles) !== JSON.stringify(newRoles))
          needsUpdate = true;
        if (JSON.stringify(existingOrg) !== JSON.stringify(newOrg))
          needsUpdate = true;
        if (
          (existingUser.country?.countryCode
            ? String(existingUser.country.countryCode).trim()
            : null) !== country
        )
          needsUpdate = true;
        const existingSupplierNo = existingUser.supplier
          ? existingUser.supplier.supplierNo
            ? String(existingUser.supplier.supplierNo).trim()
            : null
          : null;
        if (existingSupplierNo !== supplierNo) needsUpdate = true;

        if (needsUpdate) {
          // console.log("[Bootstrap] Profile fields changed, updating user");
          const updatedUser = {
            ...existingUser,
            email: newUserData.email,
            givenName: newUserData.givenName,
            familyName: newUserData.familyName,
            roles: newUserData.roles,
            organcestors: newUserData.organcestors,
            country: newUserData.country,
            supplier: newUserData.supplier,
          };
          await updateUser(emea, updatedUser);
          // console.log("[Bootstrap] User updated successfully");
        } else {
          // console.log("[Bootstrap] No profile changes detected; skipping update");
        }
      }

      sessionStorage.setItem('bootstrap.status', 'ok');
      sessionStorage.removeItem('bootstrap.error');
      return true;
    } catch (error) {
      const msg = `Bootstrap failed: ${error.message}`;
      console.error('[Bootstrap]', msg);
      sessionStorage.setItem('bootstrap.status', 'error');
      sessionStorage.setItem('bootstrap.error', msg);
      return false;
    }
  }
}
